import React from 'react'
import { PAYMENT_METHODS, getEnabledPaymentMethods, PaymentMethodType } from '../../config/paymentMethods'

interface PaymentMethodSelectorProps {
  selectedMethod: 'getnet' | 'khipu' | 'transbank'
  onMethodChange: (method: 'getnet' | 'khipu' | 'transbank') => void
  disabled?: boolean
  showDisabled?: boolean  // Mostrar métodos deshabilitados en gris
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  disabled = false,
  showDisabled = false
}) => {
  const enabledMethods = getEnabledPaymentMethods()
  const allMethods = showDisabled ? Object.values(PAYMENT_METHODS) : enabledMethods
  const selectedConfig = PAYMENT_METHODS[selectedMethod as PaymentMethodType]

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Método de Pago *
      </label>

      {/* Botones compactos de selección */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {/* Renderizar dinámicamente métodos de pago */}
        {allMethods.map((method) => (
          <button
            key={method.id}
            type="button"
            onClick={() => onMethodChange(method.id)}
            disabled={disabled || !method.enabled}
            style={
              selectedMethod === method.id && method.enabled
                ? { borderColor: method.borderColor, backgroundColor: method.backgroundColor }
                : method.enabled
                  ? { borderColor: '#e5e7eb' }
                  : { borderColor: '#e5e7eb', opacity: 0.5 }
            }
            className={`relative border-2 rounded-lg p-3 transition-all disabled:cursor-not-allowed flex flex-col items-center gap-2 ${
              method.enabled ? 'hover:shadow-md' : 'opacity-50'
            } ${selectedMethod === method.id && method.enabled ? 'shadow-md' : 'bg-white'}`}
          >
            {/* Radio button */}
            <div
              className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                selectedMethod !== method.id || !method.enabled
                  ? 'border-gray-300'
                  : `border-opacity-100`
              }`}
              style={
                selectedMethod === method.id && method.enabled
                  ? { borderColor: method.color }
                  : {}
              }
            >
              {selectedMethod === method.id && method.enabled && (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: method.color }}
                ></div>
              )}
            </div>

            {/* Logo */}
            <div className="flex-1 flex items-center justify-center">
              {method.logo ? (
                <img
                  src={method.logo}
                  alt={method.id}
                  className="h-6 max-w-[80%]"
                />
              ) : (
                <div className="text-xl font-bold" style={{ color: method.color }}>
                  {method.id.toUpperCase()}
                </div>
              )}
            </div>

            {/* Nombre */}
            <span className="text-xs font-semibold text-gray-900 text-center">
              {method.name}
            </span>

            {/* Badge de estado */}
            {!method.enabled && (
              <span className="text-xs text-orange-600 font-medium">
                {method.inDevelopment ? '🔧 En desarrollo' : '⏳ Próximamente'}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Información detallada cuando se selecciona un método */}
      {selectedConfig && (
        <div
          className="rounded-lg p-4 border"
          style={{
            backgroundColor: `${selectedConfig.backgroundColor}40`,
            borderColor: selectedConfig.borderColor
          }}
        >
          <p
            className="text-sm font-semibold mb-3"
            style={{ color: selectedConfig.color }}
          >
            {selectedConfig.name}
          </p>

          {/* Descripción */}
          {selectedConfig.description && (
            <p className="text-sm text-gray-700 mb-3">
              {selectedConfig.description}
            </p>
          )}

          {/* Características */}
          {selectedConfig.features && selectedConfig.features.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-2">Características:</p>
              <div className="text-xs text-gray-600 space-y-1">
                {selectedConfig.features.map((feature, idx) => (
                  <p key={idx}>✓ {feature}</p>
                ))}
              </div>
            </div>
          )}

          {/* Información de seguridad */}
          <div className="border-t pt-3 mt-3">
            <p
              className="text-xs font-medium mb-2"
              style={{ color: selectedConfig.color }}
            >
              🔒 Información de Seguridad
            </p>
            <p
              className="text-xs leading-relaxed"
              style={{ color: selectedConfig.color }}
            >
              {selectedConfig.securityInfo}
            </p>
          </div>

          {/* Nota si está en desarrollo */}
          {!selectedConfig.enabled && (
            <p className="text-xs leading-relaxed mt-3 text-orange-700 bg-orange-50 p-2 rounded border border-orange-200">
              ℹ️ Este método está {selectedConfig.inDevelopment ? 'en desarrollo' : 'próximamente disponible'}.
              {selectedConfig.notes && ` ${selectedConfig.notes}`}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default PaymentMethodSelector
